import React, { type FunctionComponent } from 'react'
import { type FormContextValues } from 'react-hook-form'
import {
  collectionFormWebHeaderOptions,
  collectionFormMobileHeaderOptions,
} from 'domains/collections/components/collections-form/utils/collectionFormImageOptions'
import { type CollectionsFormData } from 'domains/collections/components/collections-form/utils/collectionsForm.types'
import {
  displayStyleOptions,
  useSelectOptionsDashMessage,
} from 'domains/collections/components/collections-form/utils/collectionsFormSelectOptions'
import { CollectionsFormSection } from 'domains/collections/components/collections-form/components/form-section/CollectionsFormSection'
import { CollectionsFormImageInput } from 'domains/collections/components/collections-form/components/form-image/CollectionsFormImageInput'
import { CollectionsFormColorInput } from 'domains/collections/components/collections-form/components/form-input/CollectionsFormColorInput'
import { CollectionsFormTextArea } from 'domains/collections/components/collections-form/components/form-input/CollectionsFormTextArea'
import { CollectionsFormInput } from 'domains/collections/components/collections-form/components/form-input/CollectionsFormInput'
import { CollectionsFormSelect } from 'domains/collections/components/collections-form/components/form-input/CollectionsFormSelect'
import { collectionsFormTestIds } from 'domains/collections/components/collections-form/utils/collectionsForm.utils'
import { CollectionsFormField } from 'domains/collections/components/collections-form/components/form-field/CollectionsFormField'
import { CollectionsFormRow } from 'domains/collections/components/collections-form/components/form-row/CollectionsFormRow'
import { CollectionsFormCheckbox } from 'domains/collections/components/collections-form/components/form-input/CollectionsFormCheckbox'

interface CollectionsHeaderSectionProps {
  register: FormContextValues<CollectionsFormData>['register']
  errors: FormContextValues<CollectionsFormData>['errors']
  setValue: FormContextValues<CollectionsFormData>['setValue']
  getValues: FormContextValues<CollectionsFormData>['getValues']
  control: FormContextValues<CollectionsFormData>['control']
  showFormFragment: boolean
}

export const CollectionsHeader: FunctionComponent<CollectionsHeaderSectionProps> = ({
  register,
  errors,
  setValue,
  getValues,
  control,
  showFormFragment,
}) => {
  const selectOptions = useSelectOptionsDashMessage(displayStyleOptions)

  const collectionHeaderContent = (
    <React.Fragment>
      <CollectionsFormRow>
        <CollectionsFormField>
          <CollectionsFormInput
            identifier="header.title"
            labelId="collectionsDomain.collectionsForm.section.header.title"
            errors={errors}
            errorMessage="collectionsDomain.collectionsForm.section.header.title.error.required"
            ref={register({ required: true })}
            data-testid={collectionsFormTestIds.headerTitleField}
          />
        </CollectionsFormField>

        <CollectionsFormField>
          <CollectionsFormSelect
            identifier="header.displayStyle"
            labelId="collectionsDomain.collectionsForm.section.header.displayStyle"
            setValue={setValue}
            errors={errors}
            options={selectOptions}
            register={register}
            required={false}
            getValues={getValues}
            control={control}
            data-testid={collectionsFormTestIds.headerDisplayStyleField}
          />
        </CollectionsFormField>
      </CollectionsFormRow>

      <CollectionsFormField>
        <CollectionsFormTextArea
          identifier="header.description"
          labelId="collectionsDomain.collectionsForm.section.header.description.text"
          placeholderId="collectionsDomain.collectionsForm.section.header.description.placeholder"
          ref={register({ required: false })}
          maxLength={300}
          data-testid={collectionsFormTestIds.headerDescriptionField}
        />
      </CollectionsFormField>

      <CollectionsFormRow>
        <CollectionsFormField>
          <CollectionsFormColorInput
            identifier="header.color"
            labelId="collectionsDomain.collectionsForm.section.header.color"
            defaultValue={getValues('header.color')}
            ref={register({ required: false })}
            errorMessage="collectionsDomain.collectionsForm.section.header.color.error.required"
            errors={errors}
            data-testid={collectionsFormTestIds.headerColorField}
          />
        </CollectionsFormField>

        <CollectionsFormField>
          <CollectionsFormColorInput
            identifier="header.backgroundColor"
            labelId="collectionsDomain.collectionsForm.section.header.backgroundColor"
            defaultValue={getValues('header.backgroundColor')}
            ref={register({ required: false })}
            errorMessage="collectionsDomain.collectionsForm.section.header.backgroundColor.error.required"
            errors={errors}
            data-testid={collectionsFormTestIds.headerBackgroundColorField}
          />
        </CollectionsFormField>
      </CollectionsFormRow>

      <CollectionsFormImageInput
        {...collectionFormWebHeaderOptions}
        ref={register({ required: true })}
        errors={errors}
        defaultUrl={getValues(collectionFormWebHeaderOptions.identifier)}
      />

      <CollectionsFormImageInput
        {...collectionFormMobileHeaderOptions}
        ref={register({ required: true })}
        errors={errors}
        defaultUrl={getValues(collectionFormMobileHeaderOptions.identifier)}
      />
    </React.Fragment>
  )

  return (
    <CollectionsFormSection titleId="collectionsDomain.collectionsForm.section.header">
      <CollectionsFormRow>
        <CollectionsFormCheckbox
          name="showHeaderFragment"
          identifier="showHeaderFragment"
          labelId="collectionsDomain.collectionsForm.section.header.toggleLabel"
          control={control}
          data-testid={collectionsFormTestIds.headerToggle}
        />
      </CollectionsFormRow>

      {showFormFragment && collectionHeaderContent}
    </CollectionsFormSection>
  )
}
